/* eslint-disable react/no-danger */
import { useMutation } from "@tanstack/react-query";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  DownloadIcon,
  GKButton,
  GKFilePreviewCard,
  GKTypography,
  Grid2,
  RiskProfileIcon,
  Show,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TermIcon,
  Typography,
  VolatileIcon,
} from "components";
import { isEmpty } from "lodash";
import toast from "react-hot-toast";
import { useAppSelector } from "store/store";
import { handleRemoveUnderscore, numberToCurrency } from "utils";
import axiosInstance from "utils/axiosInstance";
import { handleCapitalize } from "utils/common";
import { exportArrayBufferToExcel } from "utils/exportCsv";
import { matchDate } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";

const EquityBasketView = () => {
  useDocumentTitle("View Equity basket");

  const { selectedBasket: basketData } = useAppSelector(
    (state) => state?.equityBasket,
  );

  const { mutate: handleDownload, isLoading: downloadLoading } = useMutation({
    mutationKey: ["MODEL_PORTFOLIO_BASKET_REPORT_DOWNLOAD"],

    mutationFn: async () => {
      const res = await axiosInstance.get(
        `/model_portfolio/${basketData?.id}/basket_report/`,
        { responseType: "blob" },
      );

      if (res.data.type === "application/json") {
        const text = await res.data.text();
        const json = JSON.parse(text);
        throw new Error(json.message || "Failed to download");
      }

      return res;
    },

    onSuccess: (response) => {
      const baseName = basketData?.name || "NAV";
      const fileName = `${baseName}_performance_comparison`;
      exportArrayBufferToExcel(response.data, fileName);
    },

    onError: () => {
      toast.error("Failed to download portfolio NAV data.");
    },
  });

  /** ========================================================================================================= */

  return (
    <Grid2 container spacing={2} size={12}>
      <Grid2 size={12} container bgcolor={"#F8F8F8"} p={2} borderRadius={2}>
        <Grid2 size={"auto"}>
          <img
            src={basketData?.basketLogo || `/Logos/errorImg.svg`}
            alt=""
            height={60}
            width={60}
            style={{ borderRadius: 6 }}
          />
        </Grid2>
        <Grid2
          size={"auto"}
          display={"flex"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          <GKTypography fontWeight={600} fontSize={20}>
            {basketData?.name || "-"}
          </GKTypography>
          <Box display={"flex"} alignItems={"center"} gap={2}>
            <GKTypography color="#4F4F4F" fontSize={16}>
              # Basket Code&nbsp;:&nbsp;
              <span style={{ fontWeight: 600 }}>{basketData?.code || "-"}</span>
            </GKTypography>
            <GKTypography color="#BDBDBD" fontSize={16}>
              |
            </GKTypography>
            <GKTypography color="#4F4F4F" fontSize={16}>
              Creation Date&nbsp;:&nbsp;
              <span style={{ fontWeight: 600 }}>
                {basketData?.basketCreationDate || "-"}
              </span>
            </GKTypography>
            <Show.When isTrue={basketData?.basketType !== "Asset"}>
              <GKTypography color="#BDBDBD" fontSize={16}>
                |
              </GKTypography>
              <GKTypography color="#4F4F4F" fontSize={16}>
                Min Investment Value&nbsp;:&nbsp;
                <span style={{ fontWeight: 600 }}>
                  {numberToCurrency(basketData?.minInvestment) || "-"}
                </span>
              </GKTypography>
            </Show.When>
          </Box>
        </Grid2>
      </Grid2>
      <Grid2 size={7} spacing={5} container>
        <Grid2 size={12} container gap={2}>
          <GKTypography variant="primaryDark">Overview</GKTypography>
          <Show.When isTrue={!isEmpty(basketData?.overview)} showNodata>
            <div
              dangerouslySetInnerHTML={{
                __html: basketData?.overview || "",
              }}
            />
          </Show.When>
        </Grid2>
        <Grid2 size={12} container gap={2}>
          <GKTypography variant="primaryDark" width={"100%"}>
            Investment Criteria
          </GKTypography>
          <Show.When
            isTrue={
              !isEmpty(basketData?.riskProfile) ||
              !isEmpty(basketData?.volatility) ||
              !isEmpty(basketData?.timePeriod)
            }
            showNodata
          >
            {[
              {
                name: "Risk",
                value: basketData?.riskProfile[0] || "-",
                icon: RiskProfileIcon,
                withSeperator: true,
                iconColor: `var(--warning-700)`,
              },
              {
                name: "Volatility",
                value: basketData?.volatility || "-",
                icon: VolatileIcon,
                withSeperator: true,
                iconColor: "var(--orange-700)",
              },
              {
                name: "Time Horizon",
                value: basketData?.timePeriod || "-",
                icon: TermIcon,
                withSeperator: false,
                iconColor: "var(--blue-700)",
              },
            ]?.map((item) => {
              return (
                <Show.When isTrue={!isEmpty(item.value)}>
                  <Box display={"flex"} alignItems={"center"} gap={0.5}>
                    <Typography fontSize={12} color="textSecondary">
                      {item.name}:
                    </Typography>
                    <Box display={"flex"} alignItems={"center"} gap={0.5}>
                      <item.icon color={item.iconColor} />
                      <Typography fontSize={12} fontWeight={600}>
                        {handleRemoveUnderscore(
                          handleCapitalize(item?.value),
                        ) || "-"}
                      </Typography>
                    </Box>
                  </Box>
                  {item.withSeperator && (
                    <Divider orientation="vertical" sx={{ height: "20px" }} />
                  )}
                </Show.When>
              );
            })}
          </Show.When>
        </Grid2>
        <Grid2 size={12} container gap={2}>
          <GKTypography variant="primaryDark" width={"100%"}>
            Assets & Benchmark Details
          </GKTypography>
          <Show.When
            isTrue={
              !isEmpty(basketData?.constituentsType) ||
              !isEmpty(basketData?.assetClassType) ||
              !isEmpty(basketData?.comparableIndex) ||
              !isEmpty(basketData?.benchmarkName)
            }
            showNodata
          >
            <TableContainer
              sx={{
                border: "none",
                "& .MuiTableCell-root": {
                  border: "none",
                },
              }}
            >
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        Constituents Type
                      </Typography>
                      <Typography>
                        {basketData?.constituentsType || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        Asset Class
                      </Typography>
                      <Typography>
                        {basketData?.assetClassType || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        Comparable Index
                      </Typography>
                      <Typography>
                        {basketData?.comparableIndex || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        Benchmark
                      </Typography>
                      <Typography>
                        {basketData?.benchmarkName || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Show.When>
        </Grid2>
        <Grid2 size={12} container gap={2}>
          <GKTypography variant="primaryDark" width={"100%"}>
            {basketData?.basketType === "Asset"
              ? "Recommendation"
              : "Rebalance"}{" "}
            Schedule
          </GKTypography>
          <Show.When
            isTrue={
              !isEmpty(basketData?.rebalanceTime) ||
              !isEmpty(basketData?.lastRebalanceDate) ||
              !isEmpty(basketData?.nextRebalanceDate)
            }
            showNodata
          >
            <TableContainer sx={{ border: "none" }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        {basketData?.basketType === "Asset"
                          ? "Recommendation"
                          : "Rebalance"}{" "}
                        Frequency
                      </Typography>
                      <Typography>
                        {basketData?.rebalanceTime || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        Last{" "}
                        {basketData?.basketType === "Asset"
                          ? "Recommendation"
                          : "Rebalance"}{" "}
                        Date
                      </Typography>
                      <Typography>
                        {matchDate(basketData?.lastRebalanceDate) || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: "#F8F8F8",
                        padding: 24,
                      }}
                    >
                      <Typography color="#697586" fontSize={12}>
                        Next{" "}
                        {basketData?.basketType === "Asset"
                          ? "Recommendation"
                          : "Rebalance"}{" "}
                        Date
                      </Typography>
                      <Typography>
                        {matchDate(basketData?.nextRebalanceDate) || "N/A"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Show.When>
        </Grid2>
        <Grid2 size={12} container gap={2}>
          <Typography width={"100%"} variant="primaryDark">
            Investment Strategies
          </Typography>
          <Show.When
            showNodata
            isTrue={basketData?.investmentStyle?.length !== 0}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
              }}
            >
              {basketData?.investmentStyle?.map((dataItem) => {
                return (
                  <Chip
                    style={{
                      borderRadius: 6,
                      padding: "8px",
                      height: "unset",
                    }}
                    label={dataItem || "-"}
                  />
                );
              })}
            </div>
          </Show.When>
        </Grid2>
      </Grid2>
      <Grid2 size={1} />
      <Grid2 size={4}>
        <Grid2 container spacing={2} size={8}>
          <Grid2 size={12} container alignItems={"center"} spacing={1}>
            <Avatar />
            <Box display={"flex"} flexDirection={"column"}>
              <Typography variant="primaryLight" textTransform={"capitalize"}>
                Managed By
              </Typography>
              <Typography
                variant="primaryDark"
                fontSize={16}
                textTransform={"capitalize"}
              >
                {basketData?.managedBy || "-"}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 size={12}>
            <Grid2 size={12}>
              <GKButton
                variant="contained"
                startIcon={!downloadLoading && <DownloadIcon />}
                onClick={() => handleDownload()}
                disabled={!basketData?.id || downloadLoading}
              >
                {downloadLoading ? "Downloading..." : "Download basket report"}
              </GKButton>
            </Grid2>
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="primaryLight">Fact Sheet</Typography>
            <Show.When isTrue={!isEmpty(basketData?.factSheet)} showNodata>
              {[basketData?.factSheet]?.map((dataItems) => {
                return (
                  <GKFilePreviewCard
                    index={1}
                    data={{
                      name: "Factsheet.pdf",
                      file: dataItems,
                      uploadedBy: "",
                      createdOn: "",
                      id: 0,
                      visible: true,
                    }}
                    size="-"
                  />
                );
              })}
            </Show.When>
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="primaryLight">Research Files</Typography>
            <Show.When
              isTrue={basketData?.researchFiles?.length > 0}
              showNodata
            >
              {basketData?.researchFiles?.map((dataItems) => {
                return (
                  <GKFilePreviewCard
                    index={1}
                    data={dataItems || ""}
                    size="-"
                  />
                );
              })}
            </Show.When>
          </Grid2>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default EquityBasketView;
